/**
 * Wire schemas for C-CDA ingest (`BACKLOG.md` N4's ingest half). The actual XML parsing
 * (`fast-xml-parser`) lives server-side (`server/src/clinical/ccdaIngestService.ts`) — this file
 * only carries the validated candidate shape both the parser's output and the upload/list routes
 * agree on, matching `licenseScanFields.ts`'s own split between "wire shape" (here) and "how it gets
 * produced" (`licenseScanParser.ts`).
 *
 * A candidate is deliberately a flat, generic shape covering all four record types this pass
 * extracts (`problem`/`allergy`/`medication`/`immunization`) rather than a per-type union — the four
 * source C-CDA sections carry different fields (a problem's ICD-10 code vs. a medication's dose/
 * route), and `detail` is where a type's secondary fields land as a short human-readable string
 * rather than this schema growing a new optional column per record type. A reviewer reads `detail`
 * the same way they would read `chartExportService.ts`'s own PDF table row for the same record type.
 */

import { z } from 'zod';

export const CCDA_IMPORT_RECORD_TYPES = [
  'problem',
  'allergy',
  'medication',
  'immunization',
] as const;
export type CcdaImportRecordType = (typeof CCDA_IMPORT_RECORD_TYPES)[number];

export const ccdaImportCandidateSchema = z.strictObject({
  recordType: z.enum(CCDA_IMPORT_RECORD_TYPES),
  /** The primary human-readable label — a problem's description, an allergy's substance, a
   * medication's name, a vaccine's name. Never empty; a section entry with no usable text is a
   * parse warning instead of a zero-content candidate. */
  description: z.string().min(1).max(500),
  /** A coded value the source document carried for this entry (an ICD-10 code, for example) — `null`
   * when the source used `nullFlavor` instead, which is common (see `ccdaService.ts`'s own header on
   * how little coded vocabulary is stored in this schema even for locally-authored records). */
  code: z.string().max(64).nullable(),
  codeSystemName: z.string().max(120).nullable(),
  /** A calendar date relevant to this candidate (onset, administered date, medication start) —
   * `null` when the source didn't carry one for this entry. */
  date: z.string().max(10).nullable(),
  /** Secondary fields as free text — see this file's own header for why this isn't a second schema
   * per record type. `null` when the record type has nothing further to show. */
  detail: z.string().max(500).nullable(),
});
export type CcdaImportCandidate = z.infer<typeof ccdaImportCandidateSchema>;

export const CCDA_IMPORT_STATUSES = ['pending', 'reviewed'] as const;
export type CcdaImportStatus = (typeof CCDA_IMPORT_STATUSES)[number];

/** Which external EHR a document was exported from (`BACKLOG.md` I18). Purely informational — the
 * parser branches on none of these; a real C-CDA is a standards-based document (§170.315(b)(1))
 * regardless of which certified system generated it, confirmed against Tebra/Kareo's, DrChrono's,
 * ModMed's, and eClinicalWorks's own published documentation, all four of which export standard
 * C-CDA/CCD ("Data Portability" in eClinicalWorks's case, a feature since at least 2016, plus a
 * separate FHIR API and Bulk Data/EHI export per `emr-po` A10's own certification research) as part
 * of their own ONC certification. `'other'` covers any source not named here. */
export const CCDA_IMPORT_SOURCE_SYSTEMS = [
  'tebra_kareo',
  'drchrono',
  'modmed',
  'eclinicalworks',
  'other',
] as const;
export type CcdaImportSourceSystem = (typeof CCDA_IMPORT_SOURCE_SYSTEMS)[number];

// --- Staff: upload -----------------------------------------------------------------------------

/** Raw XML as a string field, not a multipart upload — every other document-shaped payload in this
 * codebase (signature strokes, license-scan candidate fields) already travels as JSON over the
 * ordinary session-authenticated route surface, and a C-CDA document is well within JSON-string
 * size norms for this app's existing body-size handling. A real multipart path is easy to add later
 * without changing this schema's own shape. */
export const uploadCcdaImportRequestSchema = z.strictObject({
  xml: z.string().min(1).max(2_000_000),
  sourceFilename: z.string().max(255).nullable().optional(),
  sourceSystem: z.enum(CCDA_IMPORT_SOURCE_SYSTEMS).nullable().optional(),
});
export type UploadCcdaImportRequest = z.infer<typeof uploadCcdaImportRequestSchema>;

export const ccdaImportResponseSchema = z.strictObject({
  id: z.uuid(),
  patientId: z.uuid(),
  sourceFilename: z.string().nullable(),
  sourceSystem: z.enum(CCDA_IMPORT_SOURCE_SYSTEMS).nullable(),
  extractedRecords: z.array(ccdaImportCandidateSchema),
  parseWarnings: z.array(z.string()),
  status: z.enum(CCDA_IMPORT_STATUSES),
  createdAt: z.string(),
});
export type CcdaImportResponse = z.infer<typeof ccdaImportResponseSchema>;

export const uploadCcdaImportResponseSchema = z.strictObject({ import: ccdaImportResponseSchema });
export type UploadCcdaImportResponse = z.infer<typeof uploadCcdaImportResponseSchema>;

export const listCcdaImportsResponseSchema = z.strictObject({
  imports: z.array(ccdaImportResponseSchema),
});
export type ListCcdaImportsResponse = z.infer<typeof listCcdaImportsResponseSchema>;

export const CCDA_IMPORT_ERROR_CODES = [
  'invalid_request',
  'not_found',
  'forbidden',
  'unparseable',
  'audit_unavailable',
] as const;
export type CcdaImportErrorCode = (typeof CCDA_IMPORT_ERROR_CODES)[number];
